/**
 * Keeps the address bar and the editor's view in step.
 *
 * THE PATH NAMES WHAT IS ON SCREEN, which for a merged document is the
 * PROJECT and not the member whose session is hosting it. It used to be the
 * member either way, with `?combined=<projectTdoId>` riding alongside — so
 * which file was in the path was incidental, and removing that file from the
 * set broke the link. `currentViewTdoId` is the one answer to "what should
 * the URL say", and the route saga's re-entry guard asks it too, so the two
 * cannot disagree about whether a navigation is needed.
 */
import { call, put, select, takeLatest } from 'typed-redux-saga/macro';
import { redirect } from 'redux-first-router';
import type { UnknownAction } from 'redux';
import {
  ROUTE_DOCUMENT_DETAILS,
  selectCurrentRoutePayload,
  selectRouteQuery,
  selectRouteType,
} from '@common-modules/routing';
import { SET_COMBINED_PREVIEW } from '@redact-modules/projects/actions';
import { editorQuery } from '../editorQueryKeys';
import { SET_TAB } from '../actions';
import {
  selectActiveTab,
  selectPendingDocument,
  selectSession,
} from '../selectors';
import { selectPreviewingCombinedFor } from '@redact-modules/projects/selectors';

// Re-exported so the callers that already read them from here keep working;
// they LIVE in `../editorQueryKeys`, which imports nothing. See the note there.
export {
  TAB_QUERY_KEY,
  COMBINED_QUERY_KEY,
  REDACTED_TAB_QUERY_VALUE,
  editorQuery,
} from '../editorQueryKeys';

type Query = Record<string, string | undefined> | undefined;

const sameQuery = (a: Record<string, string>, b: Query): boolean => {
  const bEntries = Object.entries(b ?? {}).filter(
    ([, value]) => value !== undefined
  );
  if (Object.keys(a).length !== bEntries.length) {
    return false;
  }
  return bEntries.every(([key, value]) => a[key] === value);
};

/**
 * WHICH TDO THE URL SHOULD NAME for what is on screen right now, or
 * `undefined` while no session is open.
 *
 * The project when its output is what the reviewer is looking at, and the
 * session's own document otherwise. Shared with the route saga's re-entry
 * guard — one asks "where should we be?" to go there, the other asks it to
 * decide there is nowhere to go — so a navigation and the decision not to
 * navigate cannot be made on different reasoning.
 *
 * THE MERGED PREVIEW IS A REDACTED-TAB STATE. On the editor tab the project
 * has nothing to show — the tab belongs to the host member's own document —
 * so the path follows the session there. The pointer is left alone rather
 * than cleared, so returning to the redacted tab brings the merged document
 * straight back; what does not survive is a RELOAD from the editor tab, which
 * lands on the host member and its own output. That is the honest reading of
 * a URL that names the member.
 */
export function* currentViewTdoId() {
  const session = yield* select(selectSession);
  if (!session) {
    return undefined;
  }
  const activeTab = yield* select(selectActiveTab);
  const previewingCombinedFor = yield* select(selectPreviewingCombinedFor);
  return previewingCombinedFor && activeTab === 'redacted-file'
    ? { tdoId: String(previewingCombinedFor), isProject: true }
    : { tdoId: String(session.tdoId), isProject: false };
}

export function* writeEditorQuery() {
  // Only this route carries these params. The tab action is dispatched from
  // the document editor, but the store outlives the route.
  const routeType = yield* select(selectRouteType);
  if (routeType !== ROUTE_DOCUMENT_DETAILS.type) {
    return;
  }
  const payload = yield* select(selectCurrentRoutePayload);
  const { tdoId, caseId } = (payload ?? {}) as {
    tdoId?: string;
    caseId?: string;
  };
  if (!tdoId) {
    return;
  }
  /**
   * NOT WHILE A SWITCH IS IN FLIGHT.
   *
   * `pendingDocument` is set by `SWITCH_DOCUMENT_STARTED` and cleared on
   * `OPEN_DOCUMENT_SUCCESS`, so this is exactly the window in which the
   * session still holds the OUTGOING document while the route already names
   * the incoming one. Writing then rewrites the path back to the document
   * being left — the switch and this saga fighting over the URL, which landed
   * the reviewer on the previous file with the next one's name on it.
   *
   * The old guard got this for free by refusing whenever the session and the
   * route disagreed. That test had to go — on a merged document they disagree
   * by design, the route naming the project and the session a member — so the
   * part of it that was load-bearing is restored here, on its own terms.
   *
   * Nothing is lost by standing down: a switch changes the route itself, so
   * the URL is already right and there is nothing for this to fix.
   */
  const switching = yield* select(selectPendingDocument);
  if (switching) {
    return;
  }
  /**
   * AND WAIT FOR SOMETHING TO BE OPEN AT ALL, which is the other half of what
   * the old guard was doing. `currentViewTdoId` is undefined only then.
   */
  const wanted = yield* call(currentViewTdoId);
  if (!wanted) {
    return;
  }
  const current = (yield* select(selectRouteQuery)) as Query;
  const activeTab = yield* select(selectActiveTab);
  const query = editorQuery(current, activeTab, wanted.isProject);
  // Nothing to say, so say nothing: a redirect that changes neither the path
  // nor the query is pure history churn.
  if (wanted.tdoId === String(tdoId) && sameQuery(query, current)) {
    return;
  }
  const action = ROUTE_DOCUMENT_DETAILS({
    tdoId: wanted.tdoId,
    caseId,
  }) as unknown as {
    type: string;
    payload: unknown;
    meta?: unknown;
  };
  yield* put(
    redirect({
      ...action,
      // Cast for the same reason the editor's own redirect does: these route
      // actions are typed by the app's action creators, not by
      // redux-first-router's `Meta`, which demands a `location` the router
      // fills in itself.
      meta: { ...(action.meta ?? {}), query },
    } as never) as unknown as UnknownAction
  );
}

export function* watchEditorUrlState() {
  yield* takeLatest(
    [SET_TAB.type, SET_COMBINED_PREVIEW.type],
    writeEditorQuery
  );
}

export default watchEditorUrlState;
